import { useState, useMemo, useCallback, useEffect, useRef, type KeyboardEvent } from "react";
import {
  Box,
  Text,
  Paper,
  Radio,
  Checkbox,
  Button,
  Flex,
  Stack,
  Badge,
  Center,
  Divider,
  Group,
  Progress,
  Loader,
  RingProgress,
  SimpleGrid,
  Tooltip,
  Collapse,
  Modal,
  ActionIcon,
} from "@mantine/core";
import {
  IconCheck,
  IconClock,
  IconAlertCircle,
  IconMessageCircle,
  IconAlertTriangle,
  IconQuestionMark,
  IconChevronDown,
  IconChevronUp,
  IconArrowLeft,
  IconArrowRight,
  IconX,
} from "@tabler/icons-react";

import type {
  Questionnaire,
  FullGradingResult,
  QuestionGradingResult,
  GradeCategory,
  Question,
  Answer,
  ScaleAnswer,
  PolicyAnswer,
  MultiSelectAnswer,
} from "@/utils/gradingEngine.types";
import { gradeFullQuestionnaire } from "@/utils/gradingEngine";

import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { successNotification } from "@/shared/utils/successNotification";
import { getQuestionnaireId, getQuestionnaireLabel, type QuestionnaireId } from "@/utils/questionnaireMapping";
import { getGradeDescription } from "@/data/questionnaireGradeDescriptions";

// ─── Import all questionnaires ───────────────
import q1 from "@/data/questionnaires/questionnaire_1.json";
import q2 from "@/data/questionnaires/questionnaire_2.json";
import q3 from "@/data/questionnaires/questionnaire_3.json";
import q4 from "@/data/questionnaires/questionnaire_4.json";
import q5 from "@/data/questionnaires/questionnaire_5.json";
import q6 from "@/data/questionnaires/questionnaire_6.json";

const QUESTIONNAIRE_MAP: Record<string, Questionnaire> = {
  [q1.id]: q1 as unknown as Questionnaire,
  [q2.id]: q2 as unknown as Questionnaire,
  [q3.id]: q3 as unknown as Questionnaire,
  [q4.id]: q4 as unknown as Questionnaire,
  [q5.id]: q5 as unknown as Questionnaire,
  [q6.id]: q6 as unknown as Questionnaire,
};

// ─── Props ───────────────────────────────────

type QuestionnaireTabProps = {
  matchId: string;
  collaboration: {
    id: string;
    requesterRole?: string | null;
    expectedPartnerRole?: string | null;
    requesterCompanyId: string;
    receiverCompanyId: string;
  };
  userCompanyId: string;
  autoOpenModal?: boolean;
  collaborationReadOnly?: boolean;

  onExitToCollaborationOverview?: () => void;
};

// ─── Backend response shape ──────────────────

type QuestionnaireResponseData = {
  questionnaireId: string | null;
  myAnswers: Record<string, Answer> | null;
  mySubmittedAt: string | null;
  partnerSubmittedAt: string | null;
  bothSubmitted: boolean;
  partnerAnswers: Record<string, Answer> | null;
  gradingResult: unknown | null;
};

// ─── Grade Category Helpers ──────────────────

const GRADE_CONFIG: Record<GradeCategory, { color: string; label: string; icon: React.ReactNode; tooltip: string }> = {
  alignment: {
    color: "#2563EB",
    label: "Alignment",
    icon: <IconCheck size={16} />,
    tooltip: "Areas of strong agreement between both parties",
  },
  discussion: {
    color: "#F59E0B",
    label: "Discussion",
    icon: <IconMessageCircle size={16} />,
    tooltip: "Topics requiring further conversation to align",
  },
  key_decision: {
    color: "#DC2626",
    label: "Key Decision",
    icon: <IconAlertTriangle size={16} />,
    tooltip: "Critical differences needing resolution",
  },
  clarification: {
    color: "#10B981",
    label: "Clarification",
    icon: <IconQuestionMark size={16} />,
    tooltip: "Areas lacking sufficient information",
  },
};

/** Google Forms–style shell using brand primary (dark / light blue) + yellow accents */
const Q = {
  pageBg: "var(--mantine-color-primary-0)",
  card: "#ffffff",
  brand: "var(--mantine-color-primary-6)",
  brandMuted: "var(--mantine-color-primary-7)",
  rowHover: "var(--mantine-color-primary-0)",
  rowSelected: "var(--mantine-color-primary-1)",
  rowBorder: "var(--mantine-color-primary-3)",
  divider: "var(--mantine-color-primary-1)",
  slateLine: "var(--mantine-color-slate-2)",
  yellowBar: "var(--mantine-color-yellow-4)",
} as const;

// ─── Answer label helper ─────────────────────

function getAnswerLabel(question: Question, answer: Answer | undefined): string {
  if (!answer) return "—";
  if (question.type === "multi_select") {
    const ids = (answer as MultiSelectAnswer).selectedOptionIds ?? [];
    if (ids.length === 0) return "—";
    return ids.map((id) => question.options.find((o) => o.id === id)?.label ?? id).join(", ");
  }
  const id = (answer as ScaleAnswer | PolicyAnswer).selectedOptionId;
  if (!id) return "—";
  return question.options.find((o) => o.id === id)?.label ?? id;
}

function isAnsweredForQuestion(question: Question, answer: Answer | undefined): boolean {
  if (!answer) return false;
  if (question.type === "multi_select") {
    return ((answer as MultiSelectAnswer).selectedOptionIds?.length ?? 0) > 0;
  }
  return !!(answer as ScaleAnswer | PolicyAnswer).selectedOptionId;
}

// ─── Component ───────────────────────────────

export default function QuestionnaireTab({
  matchId,
  collaboration,
  userCompanyId: _userCompanyId,
  autoOpenModal = false,
  collaborationReadOnly = false,
  onExitToCollaborationOverview,
}: QuestionnaireTabProps) {
  // ─── Resolve questionnaire from role mapping ─
  const resolvedQuestionnaireId = useMemo<QuestionnaireId | null>(() => {
    try {
      if (!collaboration.requesterRole || !collaboration.expectedPartnerRole) return null;
      return getQuestionnaireId(collaboration.requesterRole, collaboration.expectedPartnerRole);
    } catch {
      return null;
    }
  }, [collaboration.requesterRole, collaboration.expectedPartnerRole]);

  const questionnaire = resolvedQuestionnaireId ? (QUESTIONNAIRE_MAP[resolvedQuestionnaireId] ?? null) : null;

  // ─── State ─────────────────────────────────
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [responseData, setResponseData] = useState<QuestionnaireResponseData | null>(null);
  const [gradingResult, setGradingResult] = useState<FullGradingResult | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const gradingTriggered = useRef(false);
  const modalBodyRef = useRef<HTMLDivElement>(null);

  // ─── Fetch existing responses on mount ─────
  const fetchResponses = useCallback(async () => {
    setLoading(true);
    try {
      const data = await trpc.collaboration.getQuestionnaireResponses.query({ matchId });
      setResponseData(data as QuestionnaireResponseData);
      // If user has previously submitted answers, populate them
      if (data.myAnswers) {
        setAnswers(data.myAnswers as unknown as Record<string, Answer>);
      }
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => {
    fetchResponses();
  }, [fetchResponses]);

  // ─── Open modal only when user chose questionnaire (e.g. ?tab=questionnaire from profile) ─
  useEffect(() => {
    if (
      !autoOpenModal ||
      collaborationReadOnly ||
      loading ||
      !responseData ||
      responseData.mySubmittedAt ||
      !resolvedQuestionnaireId
    ) {
      return;
    }
    setCurrentSectionIndex(0);
    setModalOpen(true);
  }, [autoOpenModal, collaborationReadOnly, loading, responseData, resolvedQuestionnaireId]);

  // ─── Auto-grade when both submitted ────────
  useEffect(() => {
    if (
      !responseData?.bothSubmitted ||
      !responseData?.myAnswers ||
      !responseData?.partnerAnswers ||
      !questionnaire ||
      gradingTriggered.current
    )
      return;

    // If server already has a grading result, use it
    if (responseData.gradingResult) {
      setGradingResult(responseData.gradingResult as FullGradingResult);
      return;
    }

    // Run grading on the frontend
    gradingTriggered.current = true;
    const isRequester = collaboration.requesterCompanyId === _userCompanyId;

    const answersA = isRequester
      ? (responseData.myAnswers as Record<string, Answer>)
      : (responseData.partnerAnswers as Record<string, Answer>);
    const answersB = isRequester
      ? (responseData.partnerAnswers as Record<string, Answer>)
      : (responseData.myAnswers as Record<string, Answer>);

    try {
      const result = gradeFullQuestionnaire(questionnaire, answersA, answersB);
      setGradingResult(result);

      if (!collaborationReadOnly) {
        trpc.collaboration.saveGradingResult
          .mutate({ matchId, gradingResult: result as unknown as Record<string, unknown> })
          .catch((err) => console.error("Failed to save grading result:", err));
      }
    } catch (err) {
      console.error("Grading engine error:", err);
    }
  }, [responseData, questionnaire, matchId, collaboration.requesterCompanyId, _userCompanyId, collaborationReadOnly]);

  // ─── Toggle section expansion ─────────────
  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }, []);

  // ─── Derived state ────────────────────────
  const sections = questionnaire?.sections ?? [];
  const qId = questionnaire?.id ?? "";
  const isSubmitted = !!responseData?.mySubmittedAt;
  const partnerSubmitted = !!responseData?.partnerSubmittedAt;
  const bothSubmitted = !!responseData?.bothSubmitted;

  const totalQuestionCount = useMemo(() => sections.reduce((sum, s) => sum + s.questions.length, 0), [sections]);
  const requiredQuestionIds = useMemo(
    () => sections.flatMap((s) => s.questions.filter((q) => q.required).map((q) => q.id)),
    [sections]
  );
  const questionById = useMemo(() => {
    const m: Record<string, Question> = {};
    for (const s of sections) {
      for (const q of s.questions) {
        m[q.id] = q;
      }
    }
    return m;
  }, [sections]);

  const meaningfulAnsweredCount = useMemo(() => {
    let n = 0;
    for (const s of sections) {
      for (const q of s.questions) {
        if (isAnsweredForQuestion(q, answers[q.id])) n++;
      }
    }
    return n;
  }, [sections, answers]);

  const unansweredCount = useMemo(() => totalQuestionCount - meaningfulAnsweredCount, [totalQuestionCount, meaningfulAnsweredCount]);

  const requiredSatisfied = useMemo(
    () =>
      requiredQuestionIds.length === 0
        ? true
        : requiredQuestionIds.every((id) => {
            const q = questionById[id];
            return q ? isAnsweredForQuestion(q, answers[id]) : false;
          }),
    [requiredQuestionIds, questionById, answers]
  );

  const allQuestionsAnswered = useMemo(
    () => sections.every((s) => s.questions.every((q) => isAnsweredForQuestion(q, answers[q.id]))),
    [sections, answers]
  );

  // ─── Answer Handlers ─────────────────────

  const setAnswer = useCallback((answer: Answer) => {
    setAnswers((prev) => ({ ...prev, [answer.questionId]: answer }));
  }, []);

  const handleScaleChange = useCallback(
    (questionId: string, selectedOptionId: string) => {
      setAnswer({ questionId, selectedOptionId } as ScaleAnswer);
    },
    [setAnswer]
  );

  const handlePolicyChange = useCallback(
    (questionId: string, selectedOptionId: string) => {
      setAnswer({ questionId, selectedOptionId } as PolicyAnswer);
    },
    [setAnswer]
  );

  const handleMultiSelectToggle = useCallback((questionId: string, optionId: string) => {
    setAnswers((prev) => {
      const current = prev[questionId] as MultiSelectAnswer | undefined;
      const currentIds = current?.selectedOptionIds ?? [];
      const newIds = currentIds.includes(optionId)
        ? currentIds.filter((id) => id !== optionId)
        : [...currentIds, optionId];
      return { ...prev, [questionId]: { questionId, selectedOptionIds: newIds } as MultiSelectAnswer };
    });
  }, []);

  const clearAnswer = useCallback((questionId: string) => {
    setAnswers((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  }, []);

  const handleDismissQuestionnaire = useCallback(() => {
    setModalOpen(false);
    setSubmitConfirmOpen(false);
    onExitToCollaborationOverview?.();
  }, [onExitToCollaborationOverview]);

  // ─── Submit Handler ──────────────────────

  const performSubmit = useCallback(async () => {
    if (collaborationReadOnly || !questionnaire || !requiredSatisfied || !qId) return;

    setSubmitConfirmOpen(false);
    setSubmitting(true);
    try {
      await trpc.collaboration.submitQuestionnaire.mutate({
        matchId,
        questionnaireId: qId,
        answers,
      });
      successNotification({
        title: "Questionnaire Submitted",
        message: "Your responses have been recorded successfully.",
      });
      setModalOpen(false);
      // Refresh response data from server
      await fetchResponses();
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setSubmitting(false);
    }
  }, [collaborationReadOnly, questionnaire, requiredSatisfied, qId, matchId, answers, fetchResponses]);

  const requestSubmit = useCallback(() => {
    if (collaborationReadOnly || !questionnaire || !requiredSatisfied || !qId) return;
    if (allQuestionsAnswered) {
      void performSubmit();
    } else {
      setSubmitConfirmOpen(true);
    }
  }, [collaborationReadOnly, questionnaire, requiredSatisfied, qId, allQuestionsAnswered, performSubmit]);

  // ─── Question Renderer ───────────────────

  const renderQuestion = (question: Question, readOnly = false) => {
    const answer = answers[question.id];
    const hasAnswer =
      question.type === "multi_select"
        ? ((answer as MultiSelectAnswer | undefined)?.selectedOptionIds?.length ?? 0) > 0
        : !!(answer as ScaleAnswer | PolicyAnswer | undefined)?.selectedOptionId;

    const singleSelected =
      question.type === "multi_select"
        ? null
        : ((answer as ScaleAnswer | PolicyAnswer | undefined)?.selectedOptionId ?? null);

    const optionRowSx = (selected: boolean, interactive: boolean) =>
      ({
        borderRadius: 10,
        border: `1px solid ${selected ? Q.rowBorder : Q.slateLine}`,
        background: selected ? Q.rowSelected : Q.card,
        padding: "10px 12px",
        transition: "background 120ms ease, border-color 120ms ease, box-shadow 120ms ease",
        boxShadow: selected ? "0 0 0 1px rgba(4, 48, 92, 0.06)" : undefined,
        cursor: interactive ? "pointer" : "default",
        outline: "none",
      }) as const;

    const rowKeyActivate = (fn: () => void, disabled: boolean) => (e: KeyboardEvent) => {
      if (disabled) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        fn();
      }
    };

    return (
      <Paper
        key={question.id}
        p={0}
        mb="lg"
        radius="md"
        shadow="sm"
        style={{
          background: Q.card,
          border: `1px solid ${Q.slateLine}`,
          borderLeft: `4px solid ${Q.yellowBar}`,
          overflow: "hidden",
        }}
      >
        <Flex justify="space-between" align="flex-start" gap="md" wrap="wrap" px="lg" pt="md" pb="sm">
          <Text fw={600} fz="md" c="primary.8" style={{ flex: "1 1 240px", minWidth: 0, lineHeight: 1.45 }}>
            {question.text}
            {question.required && (
              <Text component="span" c="yellow.8" ml={6} fw={700}>
                *
              </Text>
            )}
          </Text>
          <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
            {!readOnly && hasAnswer && (
              <Button variant="subtle" size="xs" c="primary.6" px={6} onClick={() => clearAnswer(question.id)}>
                Clear response
              </Button>
            )}
            <Badge variant="outline" color="primary" size="sm" radius="sm">
              {question.type === "scale" ? "Scale" : question.type === "policy" ? "Single choice" : "Multiple choice"}
            </Badge>
          </Group>
        </Flex>

        <Box px="lg" pb="lg" pt={4}>
          {/* Scale */}
          {question.type === "scale" && (
            <Radio.Group
              value={(answer as ScaleAnswer | undefined)?.selectedOptionId ?? ""}
              onChange={(val) => !readOnly && handleScaleChange(question.id, val)}
              role="radiogroup"
              aria-label={question.text}
            >
              <Stack gap={8}>
                {question.options.map((opt) => {
                  const selected = singleSelected === opt.id;
                  const radioTabStopId = singleSelected ?? question.options[0]?.id;
                  return (
                    <Box
                      key={opt.id}
                      role="radio"
                      aria-checked={selected}
                      tabIndex={readOnly ? -1 : opt.id === radioTabStopId ? 0 : -1}
                      style={optionRowSx(selected, !readOnly)}
                      onClick={readOnly ? undefined : () => handleScaleChange(question.id, opt.id)}
                      onKeyDown={rowKeyActivate(() => handleScaleChange(question.id, opt.id), readOnly)}
                    >
                      <Radio
                        value={opt.id}
                        label={opt.label}
                        disabled={readOnly}
                        color="primary"
                        tabIndex={-1}
                        styles={{
                          root: { width: "100%", pointerEvents: "none" },
                          label: { width: "100%" },
                          radio: { pointerEvents: "none" },
                        }}
                      />
                    </Box>
                  );
                })}
              </Stack>
            </Radio.Group>
          )}

          {/* Policy */}
          {question.type === "policy" && (
            <Radio.Group
              value={(answer as PolicyAnswer | undefined)?.selectedOptionId ?? ""}
              onChange={(val) => !readOnly && handlePolicyChange(question.id, val)}
              role="radiogroup"
              aria-label={question.text}
            >
              <Stack gap={8}>
                {question.options.map((opt) => {
                  const selected = singleSelected === opt.id;
                  const radioTabStopId = singleSelected ?? question.options[0]?.id;
                  return (
                    <Box
                      key={opt.id}
                      role="radio"
                      aria-checked={selected}
                      tabIndex={readOnly ? -1 : opt.id === radioTabStopId ? 0 : -1}
                      style={optionRowSx(selected, !readOnly)}
                      onClick={readOnly ? undefined : () => handlePolicyChange(question.id, opt.id)}
                      onKeyDown={rowKeyActivate(() => handlePolicyChange(question.id, opt.id), readOnly)}
                    >
                      <Radio
                        value={opt.id}
                        label={opt.label}
                        disabled={readOnly}
                        color="primary"
                        tabIndex={-1}
                        styles={{
                          root: { width: "100%", pointerEvents: "none" },
                          label: {
                            width: "100%",
                            "&[data-disabled]": { color: "var(--mantine-color-slate-6)" },
                          },
                          radio: {
                            pointerEvents: "none",
                            "&[data-disabled]": { borderColor: "var(--mantine-color-slate-4)" },
                          },
                        }}
                      />
                    </Box>
                  );
                })}
              </Stack>
            </Radio.Group>
          )}

          {/* Multi-Select */}
          {question.type === "multi_select" && (
            <Stack gap={8} role="group" aria-label={question.text}>
              {question.options.map((opt) => {
                const selected = (answer as MultiSelectAnswer | undefined)?.selectedOptionIds?.includes(opt.id) ?? false;
                return (
                  <Box
                    key={opt.id}
                    role="checkbox"
                    aria-checked={selected}
                    tabIndex={readOnly ? -1 : 0}
                    style={optionRowSx(selected, !readOnly)}
                    onClick={readOnly ? undefined : () => handleMultiSelectToggle(question.id, opt.id)}
                    onKeyDown={rowKeyActivate(() => handleMultiSelectToggle(question.id, opt.id), readOnly)}
                  >
                    <Checkbox
                      checked={selected}
                      onChange={() => {}}
                      label={opt.label}
                      disabled={readOnly}
                      color="primary"
                      tabIndex={-1}
                      styles={{
                        root: { width: "100%", pointerEvents: "none" },
                        label: { width: "100%" },
                        input: { pointerEvents: "none" },
                        inner: { pointerEvents: "none" },
                      }}
                    />
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
      </Paper>
    );
  };

  // ─── Loading state ───────────────────────

  if (loading) {
    return (
      <Box style={{ background: Q.pageBg }} py="xl" mih="45vh">
        <Center mih="40vh">
          <Loader size="lg" color="primary" />
        </Center>
      </Box>
    );
  }

  // ─── No roles assigned (no questionnaire can be resolved) ──

  if (!resolvedQuestionnaireId || !questionnaire) {
    return (
      <Box style={{ background: Q.pageBg }} py="xl" px="md" mih="45vh">
        <Center mih="40vh">
          <Paper
            shadow="sm"
            p={0}
            maw={480}
            radius="md"
            style={{ background: Q.card, border: `1px solid ${Q.slateLine}`, overflow: "hidden" }}
          >
            <Box style={{ height: 8, background: Q.brand }} />
            <Stack align="center" gap="md" p="xl" pt="lg">
              <Box
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  background: "var(--mantine-color-yellow-1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IconAlertCircle size={32} stroke={1.75} color={Q.brand} />
              </Box>
              <Text fz="lg" fw={600} c="primary.8" ta="center">
                No questionnaire available
              </Text>
              <Text fz="sm" c="slate.6" ta="center" lh={1.55}>
                Collaboration roles have not been assigned yet. The questionnaire will appear once both parties' roles are
                determined.
              </Text>
            </Stack>
          </Paper>
        </Center>
      </Box>
    );
  }

  // ─── Both submitted → show grading results ──

  if (bothSubmitted) {
    const summary = gradingResult?.summary;
    const questionsById = sections
      .flatMap((s) => s.questions)
      .reduce((acc, q) => ({ ...acc, [q.id]: q }), {} as Record<string, Question>);
    const resultsBySection = gradingResult?.results?.reduce(
      (acc, r) => {
        if (!acc[r.sectionId]) acc[r.sectionId] = [];
        acc[r.sectionId].push(r);
        return acc;
      },
      {} as Record<string, QuestionGradingResult[]>
    );

    return (
      <Box style={{ background: Q.pageBg }} mih="50vh" py="md" px={{ base: "sm", sm: "md" }}>
        {/* Header — form-style title card */}
        <Paper
          shadow="sm"
          radius="md"
          p={0}
          mb="lg"
          style={{ background: Q.card, border: `1px solid ${Q.slateLine}`, overflow: "hidden" }}
        >
          <Box style={{ height: 10, background: Q.brand }} />
          <Flex justify="space-between" align="flex-start" wrap="wrap" gap="md" p="lg">
            <Box style={{ flex: "1 1 240px" }}>
              <Text fw={700} fz="xl" c="primary.8" lh={1.25}>
                {getQuestionnaireLabel(resolvedQuestionnaireId)}
              </Text>
              {questionnaire.description && (
                <Text fz="sm" c="slate.6" mt={8} lh={1.5}>
                  {questionnaire.description}
                </Text>
              )}
            </Box>
            <Badge variant="filled" color="yellow" size="lg" radius="sm" styles={{ root: { color: Q.brandMuted, fontWeight: 600 } }}>
              Both parties submitted
            </Badge>
          </Flex>
        </Paper>

        {/* Grading Summary */}
        {gradingResult && summary ? (
          <>
            <Paper
              shadow="sm"
              radius="md"
              p="lg"
              mb="lg"
              style={{ background: Q.card, border: `1px solid ${Q.slateLine}` }}
            >
              <Text fw={700} fz="lg" c="primary.8" mb="md">
                Compatibility summary
              </Text>
              <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="lg">
                {(["alignment", "discussion", "key_decision", "clarification"] as GradeCategory[]).map((cat) => {
                  const config = GRADE_CONFIG[cat];
                  const count = summary[cat];
                  const pct = summary.total > 0 ? Math.round((count / summary.total) * 100) : 0;
                  return (
                    <Tooltip key={cat} label={config.tooltip} withArrow multiline w={200}>
                      <Paper shadow="xs" radius="md" p="md" style={{ background: Q.card, border: `1px solid ${Q.divider}` }}>
                        <Flex align="center" gap="xl">
                          <RingProgress
                            size={110}
                            thickness={12}
                            roundCaps
                            sections={[{ value: pct, color: config.color }]}
                            label={
                              <Text fw={700} fz="sm" ta="center">
                                {pct}%
                              </Text>
                            }
                          />
                          <Box ml="xs">
                            <Text fw={600} fz="sm">
                              {config.label}
                            </Text>
                            <Text fz="xs" c="slate.5">
                              {count} questions
                            </Text>
                          </Box>
                        </Flex>
                      </Paper>
                    </Tooltip>
                  );
                })}
              </SimpleGrid>
            </Paper>

            {/* Per-section breakdown */}
            {sections.map((section) => {
              const sectionResults = resultsBySection?.[section.id] ?? [];
              const isExpanded = expandedSections.has(section.id);
              const sectionSummary = sectionResults.reduce(
                (acc, r) => {
                  acc[r.category] = (acc[r.category] || 0) + 1;
                  return acc;
                },
                {} as Record<string, number>
              );

              return (
                <Paper
                  key={section.id}
                  shadow="sm"
                  radius="md"
                  mb="md"
                  style={{ background: Q.card, border: `1px solid ${Q.slateLine}`, overflow: "hidden" }}
                >
                  {/* Section header — clickable */}
                  <Flex
                    align="center"
                    justify="space-between"
                    p="md"
                    style={{ cursor: "pointer", background: Q.rowHover }}
                    onClick={() => toggleSection(section.id)}
                  >
                    <Group gap="sm">
                      <Text fw={600} fz="md" c="primary.8">
                        {section.title}
                      </Text>
                      <Group gap={4}>
                        {(["alignment", "discussion", "key_decision", "clarification"] as GradeCategory[]).map(
                          (cat) => {
                            const count = sectionSummary[cat] || 0;
                            if (count === 0) return null;
                            return (
                              <Tooltip key={cat} label={`${GRADE_CONFIG[cat].label}: ${count}`}>
                                <Badge size="sm" variant="light" color={GRADE_CONFIG[cat].color}>
                                  {count}
                                </Badge>
                              </Tooltip>
                            );
                          }
                        )}
                      </Group>
                    </Group>
                    {isExpanded ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}
                  </Flex>

                  {/* Section questions */}
                  <Collapse in={isExpanded}>
                    <Divider color={Q.divider} />
                    <Box p="md" style={{ background: Q.pageBg }}>
                      {sectionResults.map((result) => {
                        const config = GRADE_CONFIG[result.category];
                        const description = resolvedQuestionnaireId
                          ? getGradeDescription(resolvedQuestionnaireId, result.questionId, result.category)
                          : undefined;
                        return (
                          <Paper
                            key={result.questionId}
                            shadow="xs"
                            radius="md"
                            p="md"
                            mb="sm"
                            style={{ background: Q.card, border: `1px solid ${Q.slateLine}` }}
                          >
                            <Flex justify="space-between" align="flex-start" mb="xs">
                              <Text fz="sm" fw={500} style={{ flex: 1 }}>
                                {result.questionText}
                              </Text>
                              <Badge variant="light" color={config.color} size="sm" ml="sm" style={{ flexShrink: 0 }}>
                                {config.label}
                              </Badge>
                            </Flex>
                            {description && (
                              <Text fz="xs" c="slate.6" mb="xs">
                                {description}
                              </Text>
                            )}
                            {questionsById[result.questionId] && (
                              <Flex gap="xl" mt="xs" mb="xs" wrap="wrap">
                                <Box>
                                  <Text fz="xs" fw={400} c="slate.8" mb={2}>
                                    Your Answer
                                  </Text>
                                  <Text fz="xs" c="slate.6">
                                    {getAnswerLabel(
                                      questionsById[result.questionId],
                                      responseData?.myAnswers?.[result.questionId] as Answer | undefined
                                    )}
                                  </Text>
                                </Box>
                                <Box>
                                  <Text fz="xs" fw={400} c="slate.8" mb={2}>
                                    Partner's Answer
                                  </Text>
                                  <Text fz="xs" c="slate.6">
                                    {getAnswerLabel(
                                      questionsById[result.questionId],
                                      responseData?.partnerAnswers?.[result.questionId] as Answer | undefined
                                    )}
                                  </Text>
                                </Box>
                              </Flex>
                            )}
                            <Flex gap="xs">
                              <Badge variant="outline" size="xs" color="gray">
                                {result.questionType === "scale"
                                  ? "Scale"
                                  : result.questionType === "policy"
                                    ? "Single Select"
                                    : "Multi Select"}
                              </Badge>
                            </Flex>
                          </Paper>
                        );
                      })}
                    </Box>
                  </Collapse>
                </Paper>
              );
            })}
          </>
        ) : (
          <Center py="xl">
            <Stack align="center" gap="sm">
              <Loader size="md" color="primary" />
              <Text fz="sm" c="slate.6">
                Computing compatibility results...
              </Text>
            </Stack>
          </Center>
        )}
      </Box>
    );
  }

  // ─── User already submitted, waiting for partner ──

  if (isSubmitted && !partnerSubmitted) {
    return (
      <Box style={{ background: Q.pageBg }} mih="50vh" py="md" px={{ base: "sm", sm: "md" }}>
        <Paper
          shadow="sm"
          radius="md"
          p={0}
          mb="lg"
          style={{ background: Q.card, border: `1px solid ${Q.slateLine}`, overflow: "hidden" }}
        >
          <Box style={{ height: 10, background: Q.brand }} />
          <Flex justify="space-between" align="flex-start" wrap="wrap" gap="md" p="lg">
            <Box style={{ flex: "1 1 240px" }}>
              <Text fw={700} fz="xl" c="primary.8" lh={1.25}>
                {getQuestionnaireLabel(resolvedQuestionnaireId)}
              </Text>
              {questionnaire.description && (
                <Text fz="sm" c="slate.6" mt={8} lh={1.5}>
                  {questionnaire.description}
                </Text>
              )}
            </Box>
            <Badge variant="light" color="primary" size="lg" radius="sm">
              Submitted
            </Badge>
          </Flex>
        </Paper>

        <Paper shadow="sm" radius="md" p="xl" mb="xl" style={{ background: Q.card, border: `1px solid ${Q.slateLine}` }}>
          <Center>
            <Stack align="center" gap="md">
              <Box
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  background: Q.rowSelected,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: `1px solid ${Q.rowBorder}`,
                }}
              >
                <IconClock size={36} stroke={1.5} color={Q.brand} />
              </Box>
              <Text fw={700} fz="lg" c="primary.8">
                Waiting for partner
              </Text>
              <Text fz="sm" c="slate.6" ta="center" maw={440} lh={1.55}>
                Your responses have been recorded. Results will be available once your collaboration partner submits
                their questionnaire.
              </Text>
            </Stack>
          </Center>
        </Paper>

        {/* Show submitted answers read-only */}
        <Text fw={700} fz="md" mt="lg" mb="sm" c="primary.8">
          Your submitted responses
        </Text>
        {sections.map((section) => (
          <Box key={section.id} mb="xl">
            <Text fw={600} fz="lg" mb="md" c="primary.7">
              {section.title}
            </Text>
            {section.questions.map((q) => renderQuestion(q, true))}
          </Box>
        ))}
      </Box>
    );
  }

  if (collaborationReadOnly) {
    return (
      <Box style={{ background: Q.pageBg }} py="xl" px="md" mih="45vh">
        <Center mih="40vh">
          <Paper
            shadow="sm"
            p={0}
            maw={520}
            radius="md"
            style={{ background: Q.card, border: `1px solid ${Q.slateLine}`, overflow: "hidden" }}
          >
            <Box style={{ height: 8, background: Q.brand }} />
            <Stack align="center" gap="md" p="xl">
              <IconAlertCircle size={44} stroke={1.5} color={Q.brand} />
              <Text fz="lg" fw={700} c="primary.8" ta="center">
                Questionnaire is read-only
              </Text>
              <Text fz="sm" c="slate.6" ta="center" lh={1.55}>
                The active collaboration period ended 45 days after both parties paid. New submissions and edits are
                disabled. If both companies already submitted, open this tab to view your compatibility results.
              </Text>
            </Stack>
          </Paper>
        </Center>
      </Box>
    );
  }

  // ─── Active questionnaire (user hasn't submitted yet) ──

  const progressPct =
    totalQuestionCount > 0 ? Math.round((meaningfulAnsweredCount / totalQuestionCount) * 100) : 0;
  const currentSection = sections[currentSectionIndex];
  const isFirstSection = currentSectionIndex === 0;
  const isLastSection = currentSectionIndex === sections.length - 1;

  // Per-section answer counts
  const currentSectionQuestions = currentSection?.questions ?? [];
  const currentSectionAnswered = currentSectionQuestions.filter((q) =>
    isAnsweredForQuestion(q, answers[q.id])
  ).length;
  const currentSectionTotal = currentSectionQuestions.length;

  const scrollToTop = () => {
    setTimeout(() => {
      modalBodyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const goToNextSection = () => {
    if (!isLastSection) {
      setCurrentSectionIndex((prev) => prev + 1);
      scrollToTop();
    }
  };

  const goToPrevSection = () => {
    if (!isFirstSection) {
      setCurrentSectionIndex((prev) => prev - 1);
      scrollToTop();
    }
  };

  const openModal = () => {
    setCurrentSectionIndex(0);
    setModalOpen(true);
  };

  // ─── Questionnaire form content (reused inside modal) ──

  const sectionProgressPct =
    currentSectionTotal > 0 ? Math.round((currentSectionAnswered / currentSectionTotal) * 100) : 0;

  const questionnaireForm = (
    <Box ref={modalBodyRef}>
      {/* ── Title + section (Google Forms–style header card) ── */}
      <Paper
        shadow="sm"
        radius="md"
        p={0}
        mb="xl"
        style={{
          background: Q.card,
          border: `1px solid ${Q.slateLine}`,
          borderLeft: `4px solid ${Q.yellowBar}`,
          overflow: "hidden",
        }}
      >
        <Box
          style={{ background: Q.brand }}
          px={{ base: "md", sm: "xl" }}
          py={{ base: "md", sm: "lg" }}
        >
          <Text fw={700} fz={{ base: 22, sm: 28 }} lh={1.2} c="white">
            {getQuestionnaireLabel(resolvedQuestionnaireId)}
          </Text>
          {questionnaire.description && (
            <Text fz="sm" mt={10} lh={1.55} c="white" style={{ opacity: 0.9 }}>
              {questionnaire.description}
            </Text>
          )}
        </Box>

        {currentSection && (
          <Box p={{ base: "md", sm: "xl" }} pt="lg" pb="lg" style={{ background: Q.card }}>
            <Divider my="lg" color="primary" />
            <Text fw={600} fz="lg" c="primary.7">
              {currentSection.title}
            </Text>
            <Flex justify="space-between" align="center" mt={10} mb={10} gap="sm" wrap="wrap">
              <Text fz="sm" c="slate.6">
                Section {currentSectionIndex + 1} of {sections.length}
                {totalQuestionCount > 0
                  ? ` · ${totalQuestionCount} question${totalQuestionCount === 1 ? "" : "s"} total`
                  : ""}
              </Text>
              <Badge
                variant="light"
                color="primary"
                size="lg"
                radius="sm"
                styles={{ root: { fontWeight: 700, color: Q.brand, textTransform: "uppercase" } }}
              >
                {progressPct}% complete
              </Badge>
            </Flex>
            <Progress
              value={sectionProgressPct}
              size={10}
              radius="xl"
              aria-label="Section progress"
              styles={{
                root: { backgroundColor: "var(--mantine-color-primary-1)" },
                section: { backgroundColor: Q.brand },
              }}
            />
          </Box>
        )}
      </Paper>

      {/* ── Questions for current section ── */}
      {currentSectionQuestions.map((q) => renderQuestion(q))}

      {/* ── Navigation (pinned-style bar) ── */}
      <Paper
        shadow="sm"
        radius="md"
        p="md"
        mt="lg"
        mb="xl"
        style={{ background: Q.card, border: `1px solid ${Q.slateLine}` }}
      >
        <Flex justify="space-between" align="center" gap="md" wrap="wrap">
          <Button
            variant="default"
            leftSection={<IconArrowLeft size={16} />}
            onClick={goToPrevSection}
            disabled={isFirstSection}
            size="md"
            styles={{ root: { borderColor: Q.rowBorder } }}
          >
            Back
          </Button>

          <Group gap="sm">
            {isLastSection && (
              <Button variant="subtle" color="primary" size="md" onClick={() => setModalOpen(false)}>
                Skip for now
              </Button>
            )}

            {isLastSection ? (
              <Button
                disabled={!requiredSatisfied}
                loading={submitting}
                onClick={requestSubmit}
                size="md"
                color="yellow"
                variant="filled"
                styles={{ root: { color: Q.brandMuted, fontWeight: 700 } }}
              >
                Submit
              </Button>
            ) : (
              <Button
                rightSection={<IconArrowRight size={16} />}
                onClick={goToNextSection}
                size="md"
                color="primary"
                variant="filled"
              >
                Next
              </Button>
            )}
          </Group>
        </Flex>
      </Paper>
    </Box>
  );

  return (
    <>
      {/* ── Fullscreen modal for unfilled questionnaire ── */}
      <Modal
        opened={modalOpen}
        onClose={handleDismissQuestionnaire}
        fullScreen
        withCloseButton={false}
        closeOnEscape
        closeOnClickOutside={false}
        padding={0}
        styles={{
          content: { background: Q.pageBg },
          body: {
            padding: "clamp(16px, 4vw, 40px)",
            paddingTop: 16,
            maxWidth: 960,
            margin: "0 auto",
            width: "100%",
            background: Q.pageBg,
          },
          header: { display: "none" },
        }}
      >
        <Flex justify="flex-end" mb="sm">
          <ActionIcon
            variant="subtle"
            color="primary"
            size="lg"
            radius="md"
            aria-label="Close questionnaire and return to collaboration overview"
            onClick={handleDismissQuestionnaire}
          >
            <IconX size={22} stroke={2} />
          </ActionIcon>
        </Flex>
        {questionnaireForm}
      </Modal>

      <Modal
        opened={submitConfirmOpen}
        onClose={() => setSubmitConfirmOpen(false)}
        title={
          <Text fw={700} fz="md" c="primary.8">
            Unanswered questions
          </Text>
        }
        centered
        overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
        styles={{
          header: { borderBottom: `1px solid ${Q.divider}`, background: Q.card },
          body: { background: Q.card },
        }}
      >
        <Text fz="sm" c="slate.6" mb="lg" lh={1.55}>
          You still have {unansweredCount} unanswered question{unansweredCount === 1 ? "" : "s"}. Do you want to submit
          anyway?
        </Text>
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={() => setSubmitConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            loading={submitting}
            onClick={() => void performSubmit()}
            color="yellow"
            variant="filled"
            styles={{ root: { color: Q.brandMuted, fontWeight: 700 } }}
          >
            Submit anyway
          </Button>
        </Group>
      </Modal>

      {/* ── Tab content when modal is closed (skipped) ── */}
      <Box style={{ background: Q.pageBg }} py="xl" px="md" mih="50vh">
        <Center mih="40vh">
          <Paper
            shadow="sm"
            p={0}
            maw={480}
            radius="md"
            style={{ background: Q.card, border: `1px solid ${Q.slateLine}`, overflow: "hidden" }}
          >
            <Box style={{ height: 10, background: Q.brand }} />
            <Stack align="center" gap="md" p="xl">
              <Box
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  background: Q.rowHover,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IconAlertCircle size={30} stroke={1.75} color={Q.brand} />
              </Box>
              <Text fz="lg" fw={700} c="primary.8" ta="center">
                Questionnaire not completed
              </Text>
              <Text fz="sm" c="slate.6" ta="center" maw={400} lh={1.55}>
                Please complete the questionnaire to proceed with the collaboration assessment.
              </Text>
              <Button mt="xs" onClick={openModal} color="primary" variant="filled" size="md">
                Open questionnaire
              </Button>
            </Stack>
          </Paper>
        </Center>
      </Box>
    </>
  );
}
